import {useEffect, useMemo, useRef, useState} from "react";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {MapRef} from "react-map-gl";
import * as turf from "@turf/turf";
import {Stack} from "@mui/material";
import {BaseMap} from "./BaseMap";
import {selectedEntityAtom} from "../../store/filterSelectionStore";
import {fitMapToBbox, syncMaps} from "../../utils/mapboxUtils";
import {
    maderaMainMapSelectedLayersAtom,
    mainMapSelectedLayersAtom,
    splitMapSelectedLayersAtom,
} from "../../store/mapSelectedLayersStore";
import {activeDateLoadable, splitViewDateLoadable, splitViewDateUserSelectionAtom} from "../../store/activeDateStore";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {
    activeCompanyResultsLayersLoadable,
    activeCompanySplitViewResultsLayersLoadable,
} from "../../store/activeCompanyResultsLayersStore";
import {useFieldsGeojson} from "../../hooks/fieldsGeojsonHooks";
import {isMaderaCompany} from "../../utils/maderaUtils";
import {usePrevious} from "../../hooks/previousHooks";
import {useResourceValueOrDefault} from "../../hooks/resourceHooks";
import {activeScoutingNotesAtomLoadable} from "../../store/activeScoutingNotesStore.ts";
import {useActiveField} from "../../store/activeFieldStore.ts";
import {OrderStatus} from "../../types/domain/orderStatus.ts";
import scoutingNoteIcon from "../../assets/icons/map-scouting-note.png";
import pin from "../../assets/icons/map-pin.png";
import {activeMapFeatureAtom} from "../../store/activeMapFeatureStore.ts";
import {FieldResultLayersProps} from "../../types/domain/fieldResult.ts";
import {Dayjs} from "dayjs";
import {useCompany} from "../../hooks/companiesHooks.ts";
import {useActiveCompanyFields} from "../../hooks/activeCompanyFieldsHooks.ts";

export const MapView: React.FC = () => {
    const company = useCompany();
    const isMadera = company?.data && isMaderaCompany(company?.data);
    const [mainMapRef, setMainMapRef] = useState<MapRef | null>(null);
    const [splitMapRef, setSplitMapRef] = useState<MapRef | null>(null);
    const [isSplitEnabled, setIsSplitEnabled] = useState<boolean>(false);
    const [syncOffFn, setSyncOffFn] = useState<(() => void) | null>(null);
    const fieldsState = useActiveCompanyFields();
    const fields = useMemo(() => (fieldsState.isSuccess ? fieldsState.data.fields : []), [fieldsState]);
    const mapByFarmId = useMemo(() => (fieldsState.isSuccess ? fieldsState.data.mapByFarmId : {}), [fieldsState]);
    const selectedEntity = useAtomValue(selectedEntityAtom);
    const [, setActiveMapFeature] = useAtom(activeMapFeatureAtom);

    let selectedLayersAtom;
    if (isMadera) {
        selectedLayersAtom = maderaMainMapSelectedLayersAtom;
    } else {
        selectedLayersAtom = mainMapSelectedLayersAtom;
    }

    const [mainMapSelectedLayers, setMainMapSelectedLayers] = useAtom(selectedLayersAtom);
    const [splitMapSelectedLayers, setSplitMapSelectedLayers] = useAtom(splitMapSelectedLayersAtom);
    const splitViewDate = useLoadableValueOrDefault(splitViewDateLoadable, null);
    const setSplitViewDate = useSetAtom(splitViewDateUserSelectionAtom);
    const mainResultsLayersResource = useAtomValue(activeCompanyResultsLayersLoadable);
    const splitViewResultsLayersResource = useAtomValue(activeCompanySplitViewResultsLayersLoadable);
    const splitViewResultsLayers = useResourceValueOrDefault(splitViewResultsLayersResource, null);
    const scoutingNotes = useLoadableValueOrDefault(activeScoutingNotesAtomLoadable, undefined);
    const activeField = useActiveField();
    const geojson = useFieldsGeojson();
    const enableMaderaFarmUnitsLayer = company?.data ? isMaderaCompany(company?.data) : false;
    const mainDateCache = useRef<Dayjs | null>(null);
    const mainDate = useLoadableValueOrDefault(activeDateLoadable, mainDateCache.current);
    const mainResultsLayersCache = useRef<Record<string, FieldResultLayersProps> | null>(null);
    const mainResultsLayers = useResourceValueOrDefault(mainResultsLayersResource, mainResultsLayersCache.current);

    useEffect(() => {
        if (mainDate !== null) mainDateCache.current = mainDate;
    }, [mainDate]);

    useEffect(() => {
        if (mainResultsLayers !== null) mainResultsLayersCache.current = mainResultsLayers;
    }, [mainResultsLayers]);

    // Concat to a string to avoid triggering hooks when value doesn't change
    const selectedFieldsIds = useMemo(() => {
        if (selectedEntity) {
            if (selectedEntity.entityType === "company") {
                return fields.map((f) => f.id);
            } else if (selectedEntity.entityType === "farm") {
                const fieldsOfFarm = mapByFarmId[selectedEntity.entityId] ?? [];
                return fieldsOfFarm.map((f) => f.id);
            } else {
                return [selectedEntity.entityId];
            }
        }

        return [];
    }, [fields, mapByFarmId, selectedEntity]);

    const onSplitTrigger = () => {
        if (!isSplitEnabled) {
            setActiveMapFeature(null);
        }
        setIsSplitEnabled(!isSplitEnabled);
    };

    useEffect(() => {
        if (mainMapRef && splitMapRef) {
            splitMapRef.jumpTo({
                center: mainMapRef.getCenter(),
                zoom: mainMapRef.getZoom(),
                bearing: mainMapRef.getBearing(),
                pitch: mainMapRef.getPitch(),
            });

            setSyncOffFn(() => syncMaps(mainMapRef, splitMapRef));
        }
        if (mainMapRef) {
            mainMapRef.resize();
        }
    }, [mainMapRef, splitMapRef, setSyncOffFn]);

    useEffect(() => {
        if (!splitMapRef && syncOffFn) {
            syncOffFn();
        }
    }, [splitMapRef, syncOffFn]);

    const mapFitBounds = useMemo(() => {
        if (selectedFieldsIds && geojson !== null && geojson.features.length > 0) {
            const gjson = {
                ...geojson,
                features: geojson.features.filter((feature) =>
                    selectedFieldsIds.includes(feature.properties?.id as string),
                ),
            };
            return turf.bbox(gjson);
        } else {
            return null;
        }
    }, [geojson, selectedFieldsIds]);

    const prevMapFitBounds = usePrevious(mapFitBounds);

    const realignMap = () => {
        if (mainMapRef && mapFitBounds) {
            fitMapToBbox(mainMapRef, mapFitBounds);
        }
    };

    useEffect(() => {
        if (mainMapRef && mapFitBounds) {
            // Map fits to bbox without animation on first load, after that 1 sec animation is applied
            fitMapToBbox(mainMapRef, mapFitBounds, prevMapFitBounds === null ? 0 : 1000);
        }
    }, [mainMapRef, mapFitBounds, prevMapFitBounds]);

    const conditionalScoutingNotes = useMemo(() => {
        return activeField?.order?.state === OrderStatus.ACTIVE ? scoutingNotes : undefined;
    }, [activeField, scoutingNotes]);

    return (
        <Stack direction="row" sx={{height: "100%"}} gap="0.5rem">
            <BaseMap
                geojson={geojson}
                splitControl={isSplitEnabled ? "none" : "openSplit"}
                onSplitTrigger={onSplitTrigger}
                onMapRefChange={setMainMapRef}
                alignLegend={isSplitEnabled ? "left" : "right"}
                mapSelectedLayers={mainMapSelectedLayers}
                setMapSelectedLayers={setMainMapSelectedLayers}
                date={mainDate}
                showDateSelector={false}
                layerResults={mainResultsLayers}
                selectedFieldsIds={selectedFieldsIds}
                enableMaderaFarmUnitsLayer={enableMaderaFarmUnitsLayer}
                showRealign={true}
                showSlider={true}
                onRealign={realignMap}
                scoutingNotes={conditionalScoutingNotes}
                images={{pin, scoutingNoteIcon}}
                mapId="main-map"
            />

            {isSplitEnabled && (
                <BaseMap
                    geojson={geojson}
                    splitControl="closeSplit"
                    onSplitTrigger={onSplitTrigger}
                    onMapRefChange={setSplitMapRef}
                    alignLegend="right"
                    mapSelectedLayers={splitMapSelectedLayers}
                    setMapSelectedLayers={setSplitMapSelectedLayers}
                    date={splitViewDate}
                    setDate={setSplitViewDate}
                    showDateSelector={true}
                    layerResults={splitViewResultsLayers}
                    selectedFieldsIds={selectedFieldsIds}
                    enableMaderaFarmUnitsLayer={enableMaderaFarmUnitsLayer}
                    showRealign={false}
                    showSlider={false}
                    onRealign={realignMap}
                    scoutingNotes={conditionalScoutingNotes}
                    images={{pin, scoutingNoteIcon}}
                    mapId="split-map"
                />
            )}
        </Stack>
    );
};
